import { useEffect, useMemo, useState } from "react";
import { Empty, Modal, Timeline} from "antd";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import "./pendingStatusModal.css";
import { useFeatureStatusLinkMutation } from "../../service/apiSlices/sematicModel";
import { CheckCircleFilled, ClockCircleFilled, LoadingOutlined } from "@ant-design/icons";
import { TruncatedModalTitle } from "../dashboard/TruncatedModalTitle";
import { PendingModelTimelineTypes } from "../../utilities/types/ModelsTypes";
import { featureStatusMap, processStatusColorMap, SSC_BASE_URL } from "../../utilities/Constants/Constants";

const getStatusIcon = (status: string) => {
  switch (status) {
    case "Completed":
      return <CheckCircleFilled className="status-icon completed-icon" />;
    case "In Progress":
      return <LoadingOutlined className="status-icon in-progress-icon spinning" />;
    case "Pending":
      return <ClockCircleFilled className="status-icon pending-icon-list" />;
    case "Loading":
      return <LoadingOutlined className="status-icon loading-icon spinning" />;
    default:
      return <ClockCircleFilled className="status-icon" />;
  }
};

const PendingStatusModal = ({
  model,
  isVisible,
  handleOnChange,
  onAllStatusCompleted,
}: {
  model: any;
  isVisible: boolean;
  handleOnChange: () => void;
  onAllStatusCompleted: (
    id: number | string,
    data: any
  ) => void;
}) => {
  const { t } = useTranslation("common");
  const [statusTimeline, setStatusTimeline] = useState<any>(null);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [featureStatus] = useFeatureStatusLinkMutation();

  const loading = useSelector((state: any) => state.static.loader);

  useEffect(() => {
    if (!model?.id) return;

    const eventSource = new EventSource(SSC_BASE_URL + `${model?.id}`);
    
    eventSource.onmessage = (event) => {
      const convertedData = JSON.parse(event.data);
      updateTimeline(convertedData.data);
    };
    
    eventSource.onerror = (err) => {
      console.error('EventSource failed:', err);
      eventSource.close();
    };

    fetchPendingStatus()
    
    return () => {
      eventSource.close();
    };
  }, [model?.id]);

  const fetchPendingStatus = async () => {
    try {
      const payload = { secret_key: model.secret_key };
      featureStatus(payload).unwrap().then((response: any) => {
        if (response?.status_code === 200 && response?.data?.length > 0) {
          updateTimeline(response.data)
        }
      })
    } catch (error) {
      console.error(error);
    }
  };

  const updateTimeline = (data: any) => {
    const notCompletedStatusItem = data.some(
      (item: any) =>
        item.process_status !== "Completed"
    );

    // Find the active (in progress) item
    const inProgressIndex = data?.findIndex(
      (item: PendingModelTimelineTypes) => item.process_status === "In Progress"
    );

    setActiveItemIndex(inProgressIndex !== -1 ? inProgressIndex : null);
    setStatusTimeline(data);
    if (!notCompletedStatusItem)
      onAllStatusCompleted(model.id, {
        ...model,
        is_model_completed: true,
      });
  }

  const memoisedTimeline = useMemo(() => {
    if (statusTimeline?.length > 0) {
      let temp = [...statusTimeline]
      let checkArr = temp?.length > 0 ? temp?.sort((a: any, b: any) => a.process - b.process) : [];

      const newTimeline = loading
        ? prepareLoadingTimeline(model.format_id)
        : checkArr;

      return newTimeline?.map((item: any, index: number) => {
        const { dotColor, textColor } =
          processStatusColorMap[`${item.process_status}`];

        const animationClass =
          item.process_status === "In Progress" ? "timeline-item-active" :
            item.process_status === "Completed" ? "timeline-item-completed" :
              "timeline-item-pending";

        return {
          key: item.id,
          color: dotColor,
          dot: getStatusIcon(item.process_status),
          className: `timeline-item ${animationClass} ${index === activeItemIndex ? 'current-active' : ''}`,
          children: (
            <div className="timeline-content">
              <p className="mb-0 status-name-li">
                {item.status_name}
              </p>
              <p style={{ color: textColor }} className="process-status">
                {item.process_status}
              </p>
            </div>
          ),
        };
      });
    }
  }, [statusTimeline, loading]);

  if (!statusTimeline) return null 

  return (
    <Modal
      title={false}
      footer={false}
      centered
      open={isVisible}
      onCancel={handleOnChange}
      className="pending-status-modal"
      width={600}
      mask={false}
    >
      <TruncatedModalTitle title={model.filename} />
      <div className="mt-5 row row-cols-4 w-100">
        <div className="col-3"></div>
        <div className="col-9">
          {(loading || memoisedTimeline?.length > 0) && (
            <Timeline items={memoisedTimeline} />
          )}
        </div>
      </div>
      {!(loading || memoisedTimeline?.length > 0) && <Empty />}
    </Modal>
  );
};

const prepareLoadingTimeline = (type: any) => {
  const statuses = featureStatusMap[type] || featureStatusMap["default"] || [];
  return statuses.map((status_name: string, idx: number) => ({
    id: idx,
    status_name,
    process: idx,
    process_status: "Loading",
    status: false,
  }));
};

export default PendingStatusModal;
